<template>
  <div class="absolute inset-0 bg-gray-100 h-full group @container">
    <div
      ref="containerRef"
      class="w-full h-full flex overflow-x-scroll snap-x snap-mandatory scrollbar-hidden"
    >
      <TIcon
        v-if="!contents.length"
        :icon="['fal', 'image']"
        size="2xl"
        class="m-auto text-gray-500"
      />

      <template
        v-for="(content, index) in contents"
        :key="getContentKey(content)"
      >
        <TSwipeImage
          v-if="isImage(content)"
          :key="content.path"
          ref="contentRefs"
          :image="content"
          @click="emit('content-clicked', index)"
        />
        <TSwipeVideo
          v-if="isVideo(content)"
          :key="content.source"
          ref="contentRefs"
          :video="content"
          @click="emit('content-clicked', index)"
        />
      </template>
    </div>

    <AtomTransition timing="instant">
      <div
        v-show="contents.length > 1 && !isMobileOrTablet"
        class="absolute top-1/2 left-4 transform -translate-y-1/2 translate-x-2 w-8 h-8 bg-white rounded-full flex items-center justify-center opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-80 hover:opacity-100"
        @click.prevent.stop="previous()"
      >
        <TIcon :icon="['fas', 'chevron-left']" />
      </div>
    </AtomTransition>

    <AtomTransition timing="instant">
      <div
        v-show="contents.length > 1 && !isMobileOrTablet"
        class="absolute top-1/2 right-4 transform -translate-y-1/2 -translate-x-2 w-8 h-8 bg-white rounded-full flex items-center justify-center opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-80 hover:opacity-100"
        @click.prevent.stop="next()"
      >
        <TIcon :icon="['fas', 'chevron-right']" />
      </div>
    </AtomTransition>

    <div
      v-if="contents.length > 1"
      ref="dotsRef"
      class="absolute bottom-4 left-1/2 transform -translate-x-1/2 max-w-14 overflow-hidden flex gap-1"
    >
      <div
        v-for="(media, index) in contents"
        :key="isImage(media) ? media.path : media.source"
        class="w-2 h-2 rounded-full bg-white shrink-0 transform transition-all duration-300"
        :class="getDotClass(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { TSwipeImage } from '#components'
import type { Nullable } from '~/types'
import type { SwipeContent, SwipeImage, SwipeVideo } from './types'

withDefaults(defineProps<{
  contents?: SwipeContent[]
}>(), {
  contents: () => [],
})

const emit = defineEmits<{
  'content-viewed': [index: number]
  'content-clicked': [index: number]
}>()

const containerRef = ref<Nullable<HTMLElement>>(null)
const contentRefs = ref<(Nullable<InstanceType<typeof TSwipeImage>>)[]>([])
const dotsRef = ref<Nullable<HTMLElement>>(null)

const { isMobileOrTablet } = useDevice()

const {
  currentIndex,
  maxIndex,
  previous,
  next,
} = useSwipeContent(
  containerRef,
  contentRefs,
  (index) => {
    emit('content-viewed', index)
  },
)

/**
 * Watches the current index and scrolls the images and dots to the correct
 * position and emits an event that the image has been viewed.
 */
watch(currentIndex, (newIndex, oldIndex) => {
  if (newIndex !== oldIndex) {
    /**
     * We should scroll the dots under the following conditions:
     * - We're going to a higher index AND
     * - The new index is greater than 2 AND
     * - The maxIndex is greater than 4
     *
     * OR
     *
     * - We're going to a lower index AND
     * - The new index is less than the maxIndex - 2 AND
     * - The maxIndex is greater than 4
     */
    if (
      (newIndex > oldIndex && newIndex > 2 && maxIndex.value > 4)
      || (newIndex < oldIndex && newIndex < maxIndex.value - 2 && maxIndex.value > 4)
    ) {
      dotsRef.value?.scrollTo({
        left: 12 * (newIndex - 2),
        behavior: 'smooth',
      })
    }
  }
})

/**
 * Returns the class for the dot at the given index. The current image has a big
 * dot, the previous and next images have medium dots, the rest are small dots.
 *
 * @param index The index of the dot
 * @returns The class for the dot
 */
function getDotClass(index: number) {
  if (index === currentIndex.value) {
    return 'scale-80 opacity-100'
  }

  if (index === currentIndex.value - 1 || index === currentIndex.value + 1) {
    return 'scale-60 opacity-80'
  }

  return 'scale-40 opacity-60'
}

function getContentKey(media: SwipeContent): string {
  if (isImage(media)) {
    return media.path
  }

  return media.source
}

function isImage(media: SwipeContent): media is SwipeImage {
  return media.type === 'image'
}

function isVideo(media: SwipeContent): media is SwipeVideo {
  return media.type === 'video'
}
</script>
